import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { getProducts, getCategories, updateProduct } from '../../../lib/api';
import { Search, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminArticlePricing() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);

  const filteredProducts = useMemo(() => {
    const normalized = search.trim().toLowerCase();
    return products.filter((product) => {
      if (selectedCategory !== 'all' && product.category_id !== selectedCategory) return false;
      if (!normalized) return true;
      return product.name.toLowerCase().includes(normalized) || (product.description || '').toLowerCase().includes(normalized);
    });
  }, [products, search, selectedCategory]);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [prods, cats] = await Promise.all([getProducts(), getCategories()]);
      setProducts(prods);
      setCategories(cats);
    } catch (error) {
      toast.error('Fehler beim Laden der Preise');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePriceUpdate = async (productId, nextPrice) => {
    setSavingId(productId);
    try {
      const product = products.find((item) => item.id === productId);
      if (!product) throw new Error('Produkt nicht gefunden');
      await updateProduct(productId, {
        name: product.name,
        category_id: product.category_id,
        description: product.description || '',
        price: Number(nextPrice),
        vat_rate: product.vat_rate || 10,
        barcode: product.barcode || null,
        print_groups: product.print_groups || [],
        modifiers: product.modifiers || [],
        external_ids: product.external_ids || {},
      });
      setProducts((current) => current.map((item) => (item.id === productId ? { ...item, price: Number(nextPrice) } : item)));
      toast.success('Preis gespeichert');
    } catch (error) {
      toast.error('Fehler beim Speichern des Preises');
    } finally {
      setSavingId(null);
    }
  };

  const categoryMap = useMemo(() => Object.fromEntries(categories.map((cat) => [cat.id, cat.name])), [categories]);

  return (
    <div className="p-6 space-y-6">
      <div className="space-y-3">
        <p className="text-sm font-semibold uppercase tracking-[0.22em] text-slate-400">Artikel</p>
        <h1 className="text-3xl font-bold text-[#132d39]">Preisstufen</h1>
        <p className="max-w-3xl text-slate-600">
          Verwalten Sie die Preise Ihrer Artikel direkt hier. Suche, Kategorie-Filter und Schnellspeicherung helfen bei der täglichen Preissteuerung.
        </p>
      </div>

      <Card className="rounded-[28px] border-slate-200 bg-white shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-xl font-bold tracking-tight text-[#314b59]">Artikelpreise</CardTitle>
          <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2">
              <Search className="w-4 h-4 text-slate-400" />
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Artikel suchen..."
                className="border-none bg-transparent p-0 text-sm"
              />
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={loadData}>
                <RefreshCw className="w-4 h-4 mr-2" />Aktualisieren
              </Button>
              <select
                value={selectedCategory}
                onChange={(event) => setSelectedCategory(event.target.value)}
                className="rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700"
              >
                <option value="all">Alle Kategorien</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>{category.name}</option>
                ))}
              </select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead>
                <tr className="bg-slate-50 text-left text-slate-500 uppercase tracking-[0.12em]">
                  <th className="px-4 py-3">Artikel</th>
                  <th className="px-4 py-3">Kategorie</th>
                  <th className="px-4 py-3 text-right">Preis</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {isLoading ? (
                  <tr><td colSpan="4" className="px-4 py-12 text-center text-slate-400">Lade Artikel…</td></tr>
                ) : filteredProducts.length === 0 ? (
                  <tr><td colSpan="4" className="px-4 py-12 text-center text-slate-400">Keine Artikel gefunden.</td></tr>
                ) : filteredProducts.map((product) => (
                  <tr key={product.id} className="bg-white hover:bg-slate-50">
                    <td className="px-4 py-4 font-medium text-slate-900">{product.name}</td>
                    <td className="px-4 py-4 text-slate-600">{categoryMap[product.category_id] || 'Unbekannt'}</td>
                    <td className="px-4 py-4 text-right text-slate-900 font-semibold tabular-nums">€{product.price.toFixed(2)}</td>
                    <td className="px-4 py-4 text-right">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        defaultValue={product.price.toFixed(2)}
                        onBlur={(event) => {
                          const nextPrice = Number(event.target.value);
                          if (Number.isFinite(nextPrice) && nextPrice >= 0 && nextPrice !== product.price) {
                            handlePriceUpdate(product.id, nextPrice);
                          }
                        }}
                        className="w-24 rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-900"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-sm text-slate-500">Änderungen werden beim Verlassen des Eingabefelds gespeichert.</p>
        </CardContent>
      </Card>
    </div>
  );
}
